import {
  isOwnerOfferReward,
  ownerOfferReward,
  type CampaignOfferView,
  type OfferTerms,
  type OfferVersionReviewState,
  type StoreOfferKind,
  type StoreOffersRequestInput,
  type StoreOffersReuse,
  type StoreOffersStatus,
  type StoreOffersView,
} from "@townsy/contracts";
import { isApiError } from "../api/client";
import { formatCoins } from "../lib/wallet";
import { validateOfferDraft, type OfferDraft, type OfferErrors } from "./offerEditor";

/**
 * "Add an offer" on one screen (ADR-0027, founder request 2026-10-20: "Also can we make it easier to "add offers" as a
 * building owner?"): the form's rules and its words, pure so they are unit-tested without a DOM. Today's defaults and
 * limits come from the server's view; each offer is checked with the advertiser editor's own rules (offerEditor) and the
 * shared contract; the server validates everything again, decides, and starts the offers by itself once they pass
 * review.
 *
 * ADR-0029 (founder decision 2026-10-20): offers are FREE for the building owner. No price, no budget, no payment:
 * Townsy gives the coins. ADR-0030 (founder 2026-10-20): no per-store daily coin limit (each visitor's own limits stay),
 * and an owner who lost a store can re-use their earlier offers on their next one ("Re-use your offers").
 * ADR-0034 (founder 2026-10-22, "yes and yes"): what visitors do is "Visit my website" or "Answer a question" (no
 * sign-up offers), and an offer gives 5 or 10 coins. Saved offers with another amount keep running until edited.
 *
 * Words: the owner reads "offer", "checking", "passed review", "needs a change", "live" and "free" here, never
 * "campaign", "creative", "placement", "approved", "rejected", "activate", "budget" or "you pay".
 */

/** The one line that says what offers cost (ADR-0029). */
export const FREE_OFFERS_LINE = "Offers are free for building owners: you never pay for them. Coins are a reward from Townsy with no cash value.";

export interface OfferForm {
  /** Form-local key (React list key). */
  key: string;
  /** The saved offer being edited, or null for a new one. */
  offerId: string | null;
  kind: StoreOfferKind;
  title: string;
  instructions: string;
  /** The owner typed their own title / instructions (a kind change then keeps them). */
  titleEdited: boolean;
  instructionsEdited: boolean;
  /** Coins per completion as typed (the chips set it). */
  rewardCoins: string;
  /** Quiz: what visitors read before the question (it should contain the answer). */
  explanation: string;
  question: string;
  choices: string[];
  correctChoice: number | null;
  // "More options" (today's defaults, today's limits)
  attemptTtlMinutes: string;
  /** Empty = no daily limit. */
  dailyCompletionLimit: string;
  minReturnSeconds: string;
  maxTries: string;
  /** The saved version's review state and number (null for a new offer). */
  saved: null | { reviewState: OfferVersionReviewState; version: number };
  /** Changed since loaded (a new offer always is). */
  dirty: boolean;
}

export interface OffersForm {
  offers: OfferForm[];
  /** REWARD_CONSENT_TEXT, ticked for exactly these offers (any change of an offer clears it). */
  consent: boolean;
  /** ADR-0030: the screen was filled in from the owner's earlier offers ("Re-use your offers"); sent with the save. */
  reusedFrom?: ReusedFrom | null;
}

/** Where a re-used screen came from (the owner's own earlier offers). */
export interface ReusedFrom {
  campaignId: string;
  address: string;
  businessName: string;
  /** Website offers left out because this store has no website yet. */
  skippedWebsiteOffers: number;
  /** ADR-0034: re-used offers whose reward was not 5 or 10 (set to 10 if it was above 10, else 5). */
  rewardsAdjusted?: number;
}

let seq = 0;
const nextKey = () => `store-offer-${++seq}`;
const clip = (s: string, n: number) => {
  const chars = Array.from(s);
  return chars.length > n ? chars.slice(0, n).join("").trimEnd() : s;
};
const nameOf = (view: Pick<StoreOffersView, "store">) => (view.store?.businessName ?? "").trim() || "our store";

/** The prefilled title and instructions for a choice, from the store's name. */
export function defaultTexts(kind: StoreOfferKind, businessName: string): { title: string; instructions: string } {
  const name = businessName.trim() || "our store";
  if (kind === "website_visit") {
    return {
      title: clip(`Visit ${name} online`, 60),
      instructions: clip(`Open the ${name} website from Townsy, have a look around, then come back here to collect your coins.`, 280),
    };
  }
  return {
    title: clip(`A quick question about ${name}`, 60),
    instructions: clip(`Read a few words about ${name}, then answer one question here in Townsy.`, 280),
  };
}

const defaultsOf = (view: Pick<StoreOffersView, "defaults">, kind: StoreOfferKind) => view.defaults[kind];

/** A new offer with today's defaults and texts from the store's name (a website visit needs the store's website). */
export function newOfferForm(view: Pick<StoreOffersView, "store" | "defaults">, kind?: StoreOfferKind): OfferForm {
  const k: StoreOfferKind = kind ?? (view.store?.websiteHost ? "website_visit" : "quiz");
  const d = defaultsOf(view, k);
  const texts = defaultTexts(k, nameOf(view));
  return {
    key: nextKey(),
    offerId: null,
    kind: k,
    title: texts.title,
    instructions: texts.instructions,
    titleEdited: false,
    instructionsEdited: false,
    rewardCoins: String(d.rewardCoins),
    explanation: clip(view.store?.description ?? "", 280),
    question: "",
    choices: ["", ""],
    correctChoice: null,
    attemptTtlMinutes: String(Math.round(d.attemptTtlSeconds / 60)),
    dailyCompletionLimit: "",
    minReturnSeconds: String(view.defaults.website_visit.minReturnSeconds),
    maxTries: String(view.defaults.quiz.maxTries),
    saved: null,
    dirty: true,
  };
}

/** A saved offer's current terms as the form (nothing changed yet). */
export function offerFormFromSaved(o: CampaignOfferView, view: Pick<StoreOffersView, "defaults">): OfferForm {
  const t = o.current.terms;
  return {
    key: nextKey(),
    offerId: o.offerId,
    kind: o.kind === "quiz" ? "quiz" : "website_visit",
    title: t.title,
    instructions: t.instructions,
    titleEdited: true,
    instructionsEdited: true,
    rewardCoins: String(t.rewardCoins),
    explanation: t.explanation ?? "",
    question: t.question ?? "",
    choices: t.choices && t.choices.length >= 2 ? [...t.choices] : ["", ""],
    correctChoice: t.correctChoice,
    attemptTtlMinutes: String(Math.round(t.attemptTtlSeconds / 60)),
    dailyCompletionLimit: t.dailyCompletionLimit === null ? "" : String(t.dailyCompletionLimit),
    minReturnSeconds: String(t.minReturnSeconds ?? view.defaults.website_visit.minReturnSeconds),
    maxTries: String(t.maxTries ?? view.defaults.quiz.maxTries),
    saved: { reviewState: o.current.reviewState, version: o.current.version },
    dirty: false,
  };
}

/** The store's earlier partner-confirmed offers (ADR-0034: owners no longer add or change them): kept as they are, removable. */
export function partnerOffersOf(view: Pick<StoreOffersView, "campaign">): CampaignOfferView[] {
  return (view.campaign?.offers ?? []).filter((o) => o.status === "active" && o.kind === "partner_callback");
}

/** The form as loaded: the saved website / question offers, or one new offer. */
export function offersFormFromView(view: StoreOffersView): OffersForm {
  const saved = (view.campaign?.offers ?? []).filter((o) => o.status === "active" && o.kind !== "partner_callback").map((o) => offerFormFromSaved(o, view));
  const offers = saved.length > 0 || partnerOffersOf(view).length > 0 ? saved : view.store ? [newOfferForm(view)] : [];
  return { offers, consent: false };
}

/** Edit one offer: any change of its terms marks it changed and clears the consent (it is per version of the terms). */
export function editOffer(form: OffersForm, key: string, patch: Partial<Omit<OfferForm, "key" | "offerId" | "saved" | "dirty">>): OffersForm {
  return {
    ...form,
    consent: false,
    offers: form.offers.map((o) => {
      if (o.key !== key) return o;
      const next = { ...o, ...patch, dirty: true };
      if ("title" in patch) next.titleEdited = true;
      if ("instructions" in patch) next.instructionsEdited = true;
      return next;
    }),
  };
}

/** Switch what visitors do on a NEW offer: texts and today's reward follow unless the owner changed them. */
export function changeKind(form: OffersForm, key: string, kind: StoreOfferKind, view: Pick<StoreOffersView, "store" | "defaults">): OffersForm {
  const o = form.offers.find((x) => x.key === key);
  if (!o || o.offerId || o.kind === kind) return form;
  const before = defaultsOf(view, o.kind);
  const after = defaultsOf(view, kind);
  const texts = defaultTexts(kind, nameOf(view));
  const next: OfferForm = {
    ...o,
    kind,
    title: o.titleEdited ? o.title : texts.title,
    instructions: o.instructionsEdited ? o.instructions : texts.instructions,
    rewardCoins: o.rewardCoins === String(before.rewardCoins) ? String(after.rewardCoins) : o.rewardCoins,
    dirty: true,
  };
  return { ...form, consent: false, offers: form.offers.map((x) => (x.key === key ? next : x)) };
}

/** Active offers of the store (partner ones included) plus the new ones in the form. */
export function offerCount(form: OffersForm, view: Pick<StoreOffersView, "campaign">): number {
  return partnerOffersOf(view).length + form.offers.length;
}

export function canAddOffer(form: OffersForm, view: Pick<StoreOffersView, "campaign" | "limits">): boolean {
  return offerCount(form, view) < view.limits.maxOffers;
}

export function addOffer(form: OffersForm, view: Pick<StoreOffersView, "store" | "defaults" | "campaign" | "limits">): OffersForm {
  if (!canAddOffer(form, view)) return form;
  const used = new Set(form.offers.map((o) => o.kind));
  const kind: StoreOfferKind = view.store?.websiteHost && !used.has("website_visit") ? "website_visit" : "quiz";
  return { ...form, consent: false, offers: [...form.offers, newOfferForm(view, kind)] };
}

/** Only an offer that is not saved yet can be removed here (saved offers use "Remove this offer"). */
export function removeOffer(form: OffersForm, key: string): OffersForm {
  const o = form.offers.find((x) => x.key === key);
  if (!o || o.offerId) return form;
  return { ...form, consent: false, offers: form.offers.filter((x) => x.key !== key) };
}

// ---------------------------------------------------------------- "Re-use your offers" (ADR-0030)

/**
 * The founder (2026-10-20): "if a business loses their store because someone else took it over, the offer should vanish
 * until they take a new building, and it can prompt them to "re-use same offers" to prevent them from having to set them
 * up again". The server offers `view.reuse` (the owner's OWN earlier offers) while this store has no running offers.
 */
export interface ReuseChoice {
  reuse: StoreOffersReuse;
  /** Offers the press fills in (website offers only when this store has a website). */
  usable: number;
  /** Website offers that can't be re-used until this store has a website. */
  needWebsite: number;
  /** The press is possible (the store is live and at least one offer can be re-used). */
  enabled: boolean;
  /** Why not, in plain words (null when enabled). */
  reason: string | null;
}

const reusableTerms = (reuse: StoreOffersReuse, view: Pick<StoreOffersView, "store">) =>
  reuse.offers.filter((t) => t.kind === "quiz" || (t.kind === "website_visit" && Boolean(view.store?.websiteHost)));

/** What "Re-use your offers" can do on this store (null: nothing to re-use). */
export function reuseChoice(view: Pick<StoreOffersView, "reuse" | "store" | "limits" | "campaign">): ReuseChoice | null {
  const reuse = view.reuse;
  if (!reuse) return null;
  const room = Math.max(0, view.limits.maxOffers - partnerOffersOf(view).length);
  const usable = Math.min(room, reusableTerms(reuse, view).length);
  const needWebsite = view.store?.websiteHost ? 0 : reuse.offers.filter((t) => t.kind === "website_visit").length;
  const reason = !view.store
    ? "Set up your store first: offers use your store's name, logo and website."
    : usable === 0
      ? needWebsite > 0
        ? "Your earlier offers send visitors to a website, and this store has no website yet. Add one with Edit your store, then re-use them."
        : "This store already has as many offers as it can."
      : null;
  return { reuse, usable, needWebsite, enabled: reason === null, reason };
}

/** "Re-use your offers from Harbor Kite Co (7 Harbor Row)?" */
export function reuseQuestion(reuse: Pick<StoreOffersReuse, "fromBusinessName" | "fromAddress">): string {
  return `Re-use your offers from ${reuse.fromBusinessName} (${reuse.fromAddress})?`;
}

/** "1 website offer can't be re-used yet: this store has no website." (null when none). */
export function reuseNeedsWebsiteLine(needWebsite: number): string | null {
  if (needWebsite <= 0) return null;
  return `${needWebsite === 1 ? "1 website offer" : `${needWebsite} website offers`} can't be re-used yet: this store has no website. Add one with Edit your store.`;
}

/**
 * One press of "Re-use these offers": the screen filled in from the earlier offers (what visitors do, title,
 * instructions, reward, the question and its answers, more options). They are NEW offers here: the consent box is NOT
 * ticked (the owner confirms these exact offers again) and they go to review when sent. A title or instructions that
 * were the earlier store's prefilled texts follow this store's name. Website offers send visitors to THIS store's
 * website (the server builds them on its live design), so they are left out while it has none.
 */
export function offersFormFromReuse(view: StoreOffersView): OffersForm {
  const choice = reuseChoice(view);
  if (!choice || !choice.enabled) return offersFormFromView(view);
  const reuse = choice.reuse;
  const name = nameOf(view);
  const offers = reusableTerms(reuse, view)
    .slice(0, choice.usable)
    .map((t): OfferForm => {
      const kind: StoreOfferKind = t.kind === "quiz" ? "quiz" : "website_visit";
      const before = defaultTexts(kind, reuse.fromBusinessName);
      const after = defaultTexts(kind, name);
      return {
        key: nextKey(),
        offerId: null,
        kind,
        title: t.title === before.title ? after.title : t.title,
        instructions: t.instructions === before.instructions ? after.instructions : t.instructions,
        titleEdited: true,
        instructionsEdited: true,
        // ADR-0034: an owner's offer gives 5 or 10 coins: another earlier amount becomes 10 if above 10, else 5.
        rewardCoins: String(ownerOfferReward(t.rewardCoins)),
        explanation: t.explanation ?? "",
        question: t.question ?? "",
        choices: t.choices && t.choices.length >= 2 ? [...t.choices] : ["", ""],
        correctChoice: t.correctChoice ?? null,
        attemptTtlMinutes: String(Math.round(t.attemptTtlSeconds / 60)),
        dailyCompletionLimit: t.dailyCompletionLimit === null ? "" : String(t.dailyCompletionLimit),
        minReturnSeconds: String(t.minReturnSeconds ?? view.defaults.website_visit.minReturnSeconds),
        maxTries: String(t.maxTries ?? view.defaults.quiz.maxTries),
        saved: null,
        dirty: true,
      };
    });
  const saved = (view.campaign?.offers ?? []).filter((o) => o.status === "active" && o.kind !== "partner_callback").map((o) => offerFormFromSaved(o, view));
  const rewardsAdjusted = reusableTerms(reuse, view)
    .slice(0, choice.usable)
    .filter((t) => !isOwnerOfferReward(t.rewardCoins)).length;
  return {
    offers: [...saved, ...offers],
    consent: false,
    reusedFrom: { campaignId: reuse.fromCampaignId, address: reuse.fromAddress, businessName: reuse.fromBusinessName, skippedWebsiteOffers: choice.needWebsite, rewardsAdjusted },
  };
}

/** ADR-0034: "Offers now give 5 or 10 coins: 1 re-used offer was set to the nearest one. Check it before you send." */
export function reuseRewardsLine(adjusted: number | undefined): string | null {
  if (!adjusted) return null;
  return `Offers now give 5 or 10 coins: ${adjusted === 1 ? "1 re-used offer was" : `${adjusted} re-used offers were`} set to the nearest one. Check ${adjusted === 1 ? "it" : "them"} before you send.`;
}

// ---------------------------------------------------------------- validation and the one button

export type OfferFieldErrors = Partial<Record<"title" | "instructions" | "rewardCoins" | "attemptTtlMinutes" | "dailyCompletionLimit" | "minReturnSeconds" | "explanation" | "question" | "choices" | "correctChoice" | "maxTries" | "kind" | "terms", string>>;

export interface OffersValidation {
  offerErrors: Record<string, OfferFieldErrors>;
  consentError: string | null;
  /** The PUT body (null when nothing needs saving, or while there are errors). */
  request: StoreOffersRequestInput | null;
  /** New or changed offers (they need the consent and a review). */
  changed: boolean;
  /** The store's offers go (again) to review even without changes ("Not sent yet" / "Needs a change"). */
  resend: boolean;
  ok: boolean;
}

const toDraft = (o: OfferForm): OfferDraft => ({
  key: o.key,
  offerId: o.offerId,
  kind: o.kind,
  title: o.title,
  instructions: o.instructions,
  rewardCoins: o.rewardCoins,
  // ADR-0029: free; validateOfferDraft({ free: true }) sends 0 cents.
  billableDollars: "0",
  attemptTtlMinutes: o.attemptTtlMinutes,
  dailyCompletionLimit: o.dailyCompletionLimit,
  minReturnSeconds: o.minReturnSeconds,
  explanation: o.explanation,
  question: o.question,
  choices: o.choices,
  correctChoice: o.correctChoice,
  maxTries: o.maxTries,
  conversionType: "signup",
  // The one consent tick of the screen is checked once, for all offers (below).
  consent: true,
  dirty: o.dirty,
});

/** The advertiser editor's words, in this screen's: an "offer", what "visitors" do. */
const plain = (msg: string) =>
  msg
    .replace(/\bthe task\b/gi, "the offer")
    .replace(/\btask\b/gi, "offer")
    .replace(/Tell residents exactly what to do\./, "Tell visitors what to do.")
    .replace(/\bresidents?\b/gi, (m) => (m.toLowerCase().endsWith("s") ? "visitors" : "visitor"));

export function validateOffersForm(form: OffersForm, view: Pick<StoreOffersView, "campaign" | "status" | "store" | "limits">): OffersValidation {
  const offerErrors: Record<string, OfferFieldErrors> = {};
  const changedOffers: Array<{ offerId: string | null; terms: OfferTerms }> = [];
  const allOffers: Array<{ offerId: string | null; terms: OfferTerms }> = [];
  for (const o of form.offers) {
    // ADR-0034: a new or changed offer gives 5 or 10 coins; an unchanged saved one keeps what it gives.
    const r = validateOfferDraft(toDraft(o), { free: true, owner: o.dirty || !o.offerId });
    const errs: OfferFieldErrors = {};
    if (!r.ok) for (const [k, v] of Object.entries(r.errors) as Array<[keyof OfferErrors, string]>) if (k !== "consent" && k !== "conversionType" && k !== "billableDollars") errs[k as keyof OfferFieldErrors] = plain(v);
    if (o.kind === "website_visit" && !view.store?.websiteHost && (o.dirty || !o.offerId)) errs.kind = "Your store has no website yet. Add one with Edit your store, or ask visitors a question instead.";
    if (o.kind === "quiz" && !o.explanation.trim()) errs.explanation = "Write a few words visitors read first; the right answer should be in them.";
    if (Object.keys(errs).length) {
      offerErrors[o.key] = errs;
      continue;
    }
    if (!r.ok) continue;
    const entry = { offerId: o.offerId, terms: r.terms };
    allOffers.push(entry);
    if (o.dirty || !o.offerId) changedOffers.push(entry);
  }
  const changed = form.offers.some((o) => o.dirty || !o.offerId);
  const status = view.status.state;
  const resend = !changed && (status === "draft" || status === "needs_change") && form.offers.length > 0;
  const consentError = (changed || resend) && !form.consent ? "Tick the box to confirm these offers." : null;
  const noOffers = form.offers.length === 0;
  const ok = !noOffers && Object.keys(offerErrors).length === 0 && !consentError;
  const body = changed ? changedOffers : resend ? allOffers : [];
  const reused = form.reusedFrom ? { reusedFromCampaignId: form.reusedFrom.campaignId } : {};
  const request: StoreOffersRequestInput | null =
    ok && body.length > 0 ? { expectedVersion: view.campaign?.version ?? null, offers: body.map((e) => ({ offerId: e.offerId, terms: { ...e.terms, billableCents: 0, consent: true } })), ...reused } : null;
  return { offerErrors, consentError, request, changed, resend, ok };
}

export type PrimaryAction = { kind: "save"; label: string; enabled: true } | { kind: "none"; label: string; enabled: false; reason: string };

/** The one button: "Send for review" whenever there is something to send (nothing is ever paid). */
export function primaryAction(form: OffersForm, view: Pick<StoreOffersView, "campaign" | "status" | "store" | "limits">, v: OffersValidation = validateOffersForm(form, view)): PrimaryAction {
  if (v.changed || v.resend) return { kind: "save", label: "Send for review", enabled: true };
  return { kind: "none", label: "Nothing to save", enabled: false, reason: view.campaign ? "Change an offer or add one to send it for review." : "Add an offer first." };
}

/** What happens after the button, in order (the live summary). */
export function nextSteps(view: Pick<StoreOffersView, "status" | "campaign"> & { quickCheck?: boolean }, v: Pick<OffersValidation, "changed" | "resend">): string[] {
  const steps: string[] = [];
  const toReview = v.changed || v.resend;
  if (toReview) steps.push(view.quickCheck ? "A quick automated check (usually under a minute) or a Townsy reviewer checks your offers." : "A Townsy reviewer checks your offers.");
  const state = view.status.state;
  if (toReview || !view.campaign) steps.push("They start by themselves once they pass. Visitors earn coins from Townsy; it's free for you.");
  else if (state === "live") steps.push("Your offers keep running.");
  else if (state === "checking") steps.push(view.status.autoStart ? "They start by themselves once they pass review." : "Once they pass review, start them in the advertiser tools.");
  else if (state === "needs_change" || state === "stopped") steps.push("Change your offers and send them for review again.");
  else steps.push("See the advertiser tools for what happens next.");
  return steps;
}

// ---------------------------------------------------------------- the one plain status

export type OffersTone = "live" | "checking" | "waiting" | "needs_change" | "not_set_up";

export interface OffersStatusCopy {
  tone: OffersTone;
  title: string;
  detail: string;
  /** What the store panel offers next to the status. */
  cta: "add_offer" | "edit_offers" | "advertiser_tools";
}

/** A server note (reviewer's words, an activation reason) in this screen's words. */
export function plainOffersNote(note: string | null): string {
  let r = (note ?? "").trim();
  r = r.replace(/\bnot approved\b/gi, "not accepted").replace(/\bapproved\b/gi, "accepted").replace(/\brejected\b/gi, "not accepted");
  r = r.replace(/\bthe campaign\b/gi, "the offers").replace(/\bcampaigns?\b/gi, "offers").replace(/\ba task\b/gi, "an offer").replace(/\btasks\b/gi, "offers").replace(/\btask\b/gi, "offer");
  r = r.replace(/\bcreatives?\b/gi, "design").replace(/\bactivat(e|ed|ing)\b/gi, (m) => (/ed$/i.test(m) ? "started" : /ing$/i.test(m) ? "starting" : "start"));
  return r;
}

const completionsWord = (n: number) => `${n} ${n === 1 ? "completion" : "completions"}`;

export function offersStatusCopy(
  status: Pick<StoreOffersStatus, "state" | "note" | "completions" | "coinsGiven" | "autoStart">,
  opts: { quickCheck?: boolean } = {},
): OffersStatusCopy {
  const n = status.completions;
  // ADR-0028: with the automated check in auto mode, clear cases are decided within about a minute.
  const quick = opts.quickCheck ? "A quick automated check usually answers within a minute; anything unclear goes to a person. " : "";
  switch (status.state) {
    case "none":
      return { tone: "not_set_up", title: "No offers yet", detail: "Free for you: visitors earn coins from Townsy for visiting your website or answering a question about your store.", cta: "add_offer" };
    case "draft":
      return { tone: "waiting", title: "Not sent for review yet", detail: "Open Edit offers and send them for review.", cta: "edit_offers" };
    case "checking":
      return {
        tone: "checking",
        title: "Checking (a Townsy reviewer looks at new offers)",
        detail: `${quick}${status.autoStart ? "They start by themselves once they pass." : "Once they pass, start them in the advertiser tools."}`,
        cta: "edit_offers",
      };
    case "ready":
      return {
        tone: "waiting",
        title: "Ready, not running yet",
        detail: status.note ? `${plainOffersNote(status.note)} Open the advertiser tools to start them.` : "They passed review. Start them in the advertiser tools.",
        cta: "advertiser_tools",
      };
    case "live":
      return {
        tone: "live",
        title: `Live: ${completionsWord(n)} so far, ${formatCoins(status.coinsGiven)} given`,
        detail: "Visitors see your offers in your store's panel and inside your store. Free for you: Townsy gives the coins.",
        cta: "edit_offers",
      };
    case "needs_change":
      return { tone: "needs_change", title: `Needs a change: ${plainOffersNote(status.note)}`, detail: "Change your offers and send them again.", cta: "edit_offers" };
    case "paused":
      return { tone: "waiting", title: "Paused by you", detail: "Resume them in the advertiser tools.", cta: "advertiser_tools" };
    case "stopped":
      return { tone: "needs_change", title: `Stopped by Townsy: ${plainOffersNote(status.note)}`, detail: "Change your offers and send them for review again.", cta: "edit_offers" };
    case "no_offers":
      return { tone: "not_set_up", title: "Removed: no offers right now", detail: "Your offers were removed, so visitors don't see any. Add an offer to start again.", cta: "add_offer" };
  }
}

/** The confirmation shown before a saved offer is removed (plain words). */
export const REMOVE_OFFER_CONFIRM = "Remove this offer? Visitors can't start it any more. Anyone already doing it may finish.";

/** After a removal: the saved offers as the server has them now, plus any new offer the owner had not sent yet. */
export function afterRemoval(form: OffersForm, fresh: StoreOffersView): OffersForm {
  const reloaded = offersFormFromView(fresh);
  const unsent = form.offers.filter((o) => !o.offerId);
  const saved = reloaded.offers.filter((o) => o.offerId);
  return { ...form, offers: [...saved, ...unsent].length ? [...saved, ...unsent] : reloaded.offers, consent: false };
}

/** The review state of one saved offer, in plain words. */
export const OFFER_REVIEW_WORDS: Record<OfferVersionReviewState, string> = { pending: "Checking", approved: "Passed review", rejected: "Needs a change" };

/** What the answer to a save says. */
export function offersSavedMessage(saved: { result: "submitted" | "unchanged" }, status: Pick<StoreOffersStatus, "state">): string {
  if (saved.result === "unchanged") return "Saved. Nothing changed.";
  if (status.state === "live") return "Saved. Your offers are live.";
  return "Sent for review. Your offers start by themselves once they pass.";
}

// ---------------------------------------------------------------- server refusals in plain words

export function offersSaveErrorMessage(err: unknown): string {
  if (!isApiError(err)) return "Couldn't reach Townsy. Check your connection and try again.";
  const msg = plainOffersNote(err.message ?? "");
  switch (err.code) {
    case "UNAUTHENTICATED":
      return "Sign in again to save your offers.";
    case "EMAIL_NOT_VERIFIED":
      return "Verify your email address first (see your Account page), then save again.";
    case "FORBIDDEN":
      return msg || "Only the current owner of this store can add offers.";
    case "CREATIVE_NOT_APPROVED":
      return msg || "Set up your store first: offers use your store's name, logo and website.";
    case "CAMPAIGN_STATE_INVALID":
      return msg || "Your offers changed somewhere else. Reload them, then save again.";
    case "RATE_LIMITED":
      return "Too many saves in a short time. Wait a minute, then try again.";
    case "IDEMPOTENCY_CONFLICT":
      return "Your last save is still being processed. Try again in a moment.";
    case "VALIDATION_FAILED":
      return msg && !/^Invalid (campaign )?request/i.test(err.message) ? msg : "Check the highlighted fields, then try again.";
    case "FEATURE_DISABLED":
      return "Offers are not available on this server yet.";
    case "NOT_FOUND":
      return "This store could not be found.";
    default:
      return "Townsy couldn't save your offers right now. Try again in a moment.";
  }
}

export function offersLoadErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 401) return "Sign in with the account that owns this store to add offers.";
    if (err.status === 403 && err.code === "FEATURE_DISABLED") return "Offers are not available on this server yet.";
    if (err.status === 403) return "Only the current owner of this store can add offers. If it was taken over, it belongs to someone else now.";
    if (err.status === 404) return "This store could not be found.";
    if (err.status === 422) return "Only stores can have offers.";
  }
  return "Couldn't load your offers. Check your connection and try again.";
}

/**
 * Words the owner must never read on this screen (ADR-0027, the ADR-0020 rule; ADR-0029: nothing about paying or a
 * budget). Used by the tests.
 */
export const OFFER_SCREEN_FORBIDDEN_WORDS = [
  /\bapproved\b/i,
  /\brejected\b/i,
  /\bcampaigns?\b/i,
  /\bcreatives?\b/i,
  /\bplacements?\b/i,
  /\badvertiser profile\b/i,
  /\bactivat/i,
  /\bbudget\b/i,
  /\byou pay\b/i,
  /\bpay \$/i,
] as const;
